import { useEffect, useMemo, useState } from "react";
import { useLang } from "../../context/LanguageContext";
import { api } from "../../lib/api";
import { Users, BedDouble, Bath, ExternalLink, ChevronLeft, ChevronRight, MapPin, Sparkles } from "lucide-react";

function getMapHref(apt) {
  if (apt?.map_url) return apt.map_url;
  if (apt?.latitude != null && apt?.longitude != null) {
    return `https://www.google.com/maps?q=${apt.latitude},${apt.longitude}`;
  }
  return null;
}

function ApartmentCard({ apt, idx, isNew, onBook }) {
  const { lang, t } = useLang();
  const [current, setCurrent] = useState(0);
  const reverse = idx % 2 === 1;
  const name = lang === "el" ? apt.name_el : apt.name_en;
  const desc = lang === "el" ? apt.description_el : apt.description_en;
  const mapHref = getMapHref(apt);
  const images = apt.images && apt.images.length > 0
    ? apt.images
    : ["https://images.unsplash.com/photo-1501117716987-c8e1ecb210ae?w=1600&auto=format&fit=crop&q=60"];
  const safeIndex = Math.min(current, images.length - 1);

  const prev = () => setCurrent((c) => (c - 1 + images.length) % images.length);
  const next = () => setCurrent((c) => (c + 1) % images.length);

  return (
    <div
      data-testid={`apartment-card-${apt.id}`}
      className={`grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 items-center ${
        reverse ? "lg:[&>*:first-child]:order-2" : ""
      }`}
    >
      <div className="lg:col-span-7">
        <div className="relative overflow-hidden rounded-2xl sm:rounded-3xl group">
          <img
            src={images[safeIndex]}
            alt={name}
            data-testid={`apartment-${apt.id}-image`}
            className="w-full h-[260px] sm:h-[420px] lg:h-[520px] object-cover transition-opacity duration-500"
            key={safeIndex}
          />
          <div className="absolute top-4 sm:top-6 left-4 sm:left-6 bg-white/90 backdrop-blur px-3 sm:px-4 py-1.5 rounded-full text-[10px] sm:text-xs tracking-widest uppercase text-[#1A2B4C]">
            €{apt.price_per_night} {t.apartments.perNight}
          </div>
          {isNew && (
            <div
              data-testid={`apartment-${apt.id}-new-badge`}
              className="absolute top-4 sm:top-6 right-4 sm:right-6 inline-flex items-center gap-1.5 bg-[#E4572E] text-white px-3 sm:px-4 py-1.5 rounded-full text-[10px] sm:text-xs tracking-widest uppercase shadow-lg animate-pulse"
            >
              <Sparkles className="w-3 h-3" /> {t.apartments.newBadge}
            </div>
          )}
          <button
            onClick={prev}
            data-testid={`apartment-${apt.id}-prev`}
            aria-label="Previous photo"
            className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/85 hover:bg-white text-[#1A2B4C] flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button
            onClick={next}
            data-testid={`apartment-${apt.id}-next`}
            aria-label="Next photo"
            className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/85 hover:bg-white text-[#1A2B4C] flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
          <div className="absolute bottom-4 right-5 bg-black/50 text-white text-xs px-3 py-1 rounded-full">
            {safeIndex + 1} / {images.length}
          </div>
        </div>
        <div className="mt-4 grid grid-cols-6 sm:grid-cols-9 gap-2">
          {images.map((img, i) => (
            <button
              key={img + i}
              onClick={() => setCurrent(i)}
              data-testid={`apartment-${apt.id}-thumb-${i}`}
              className={`relative overflow-hidden rounded-lg aspect-square transition-all ${
                safeIndex === i ? "ring-2 ring-[#1A2B4C] opacity-100" : "opacity-60 hover:opacity-90"
              }`}
            >
              <img src={img} alt={`${name} ${i + 1}`} className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      </div>

      <div className="lg:col-span-5">
        <h3
          data-testid={`apartment-${apt.id}-name`}
          className="font-serif text-2xl sm:text-3xl lg:text-4xl text-[#1A2B4C] tracking-tight mb-4 sm:mb-5"
        >
          {name}
        </h3>
        <p className="text-gray-700 leading-relaxed text-sm sm:text-base mb-6 sm:mb-8">{desc}</p>

        <div className="flex flex-wrap gap-4 sm:gap-6 mb-6 sm:mb-8 pb-6 sm:pb-8 border-b border-[#E9E2D3]">
          <div className="flex items-center gap-2 text-[#6B705C]">
            <Users className="w-4 h-4" />
            <span className="text-sm">
              {apt.capacity} {t.apartments.capacity}
            </span>
          </div>
          <div className="flex items-center gap-2 text-[#6B705C]">
            <BedDouble className="w-4 h-4" />
            <span className="text-sm">
              {apt.bedrooms} {t.apartments.bedrooms}
            </span>
          </div>
          <div className="flex items-center gap-2 text-[#6B705C]">
            <Bath className="w-4 h-4" />
            <span className="text-sm">
              {apt.bathrooms} {t.apartments.bathrooms}
            </span>
          </div>
        </div>

        <div className="flex flex-wrap gap-3">
          <button
            onClick={() => onBook?.(apt.id)}
            data-testid={`apartment-${apt.id}-book-btn`}
            className="bg-[#1A2B4C] text-white px-6 py-3 rounded-full text-sm tracking-wide hover:-translate-y-0.5 transition-transform"
          >
            {t.apartments.bookThis}
          </button>
          {apt.booking_url && (
            <a
              href={apt.booking_url}
              target="_blank"
              rel="noreferrer"
              data-testid={`apartment-${apt.id}-booking-link`}
              className="inline-flex items-center gap-1.5 border border-[#6B705C] text-[#6B705C] px-6 py-3 rounded-full text-sm hover:bg-[#6B705C] hover:text-white transition-all"
            >
              Booking.com <ExternalLink className="w-3.5 h-3.5" />
            </a>
          )}
          {apt.airbnb_url && (
            <a
              href={apt.airbnb_url}
              target="_blank"
              rel="noreferrer"
              data-testid={`apartment-${apt.id}-airbnb-link`}
              className="inline-flex items-center gap-1.5 border border-[#6B705C] text-[#6B705C] px-6 py-3 rounded-full text-sm hover:bg-[#6B705C] hover:text-white transition-all"
            >
              Airbnb <ExternalLink className="w-3.5 h-3.5" />
            </a>
          )}
          {mapHref && (
            <a
              href={mapHref}
              target="_blank"
              rel="noreferrer"
              data-testid={`apartment-${apt.id}-map-link`}
              className="inline-flex items-center gap-1.5 border border-[#6B705C] text-[#6B705C] px-6 py-3 rounded-full text-sm hover:bg-[#6B705C] hover:text-white transition-all"
            >
              <MapPin className="w-3.5 h-3.5" /> {t.apartments.viewOnMap}
            </a>
          )}
        </div>
      </div>
    </div>
  );
}

export default function Apartments({ onBook }) {
  const { t } = useLang();
  const [apartments, setApartments] = useState([]);

  useEffect(() => {
    api
      .get("/apartments")
      .then((r) => setApartments(r.data))
      .catch(() => {});
  }, []);

  // Highlight the most recently created apartment with a "New!" badge.
  // Only apartments with a `created_at` field are eligible (seeded ones aren't).
  const newestId = useMemo(() => {
    const withCreated = apartments.filter((a) => a.created_at);
    if (withCreated.length === 0) return null;
    // Consider only apartments created in the last 60 days (avoid stale "new" forever)
    const cutoff = Date.now() - 60 * 24 * 3600 * 1000;
    const recent = withCreated.filter((a) => {
      const t = Date.parse(a.created_at);
      return !isNaN(t) && t >= cutoff;
    });
    if (recent.length === 0) return null;
    recent.sort((a, b) => Date.parse(b.created_at) - Date.parse(a.created_at));
    return recent[0].id;
  }, [apartments]);

  return (
    <section
      id="apartments"
      data-testid="apartments-section"
      className="py-16 sm:py-24 lg:py-32 bg-white overflow-x-hidden"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mb-12 sm:mb-20">
          <p className="uppercase text-xs sm:text-sm tracking-[0.25em] text-[#6B705C] mb-3 sm:mb-4">
            {t.apartments.overline}
          </p>
          <h2 className="font-serif text-3xl sm:text-5xl lg:text-6xl text-[#1A2B4C] tracking-tight leading-[1.05]">
            {t.apartments.title}
          </h2>
        </div>

        <div className="space-y-16 sm:space-y-24">
          {apartments.map((apt, idx) => (
            <ApartmentCard key={apt.id} apt={apt} idx={idx} isNew={apt.id === newestId} onBook={onBook} />
          ))}
        </div>
      </div>
    </section>
  );
}
